<template>
  <div
    v-if="dialogState.open"
    class="app-dialog-backdrop"
    @click.self="onCancel"
  >
    <div class="app-dialog card shadow-sm">
      <div class="card-header d-flex align-items-center justify-content-between">
        <strong>{{ dialogState.title }}</strong>
        <button
          type="button"
          class="btn-close"
          aria-label="Cerrar"
          @click="onCancel"
        ></button>
      </div>
      <div class="card-body">
        <p class="mb-0 app-dialog-message">{{ dialogState.message }}</p>
      </div>
      <div class="card-footer d-flex justify-content-end gap-2">
        <button
          v-if="dialogState.mode === 'confirm'"
          type="button"
          class="btn btn-outline-secondary"
          @click="onCancel"
        >
          {{ dialogState.cancelText }}
        </button>
        <button
          ref="confirmButton"
          type="button"
          class="btn"
          :class="confirmButtonClass"
          @click="onConfirm"
        >
          {{ dialogState.confirmText }}
        </button>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, nextTick, onBeforeUnmount, ref, watch } from "vue";
import { closeDialog, dialogState } from "@/utils/dialogs";

export default defineComponent({
  name: "AppDialogHost",
  setup() {
    const confirmButton = ref<HTMLButtonElement | null>(null);

    const confirmButtonClass = computed(() => {
      switch (dialogState.variant) {
        case "success":
          return "btn-success";
        case "danger":
          return "btn-danger";
        case "warning":
          return "btn-warning";
        case "secondary":
          return "btn-secondary";
        default:
          return "btn-primary";
      }
    });

    const onConfirm = () => closeDialog(true);
    const onCancel = () => closeDialog(false);

    const onKeydown = (event: KeyboardEvent) => {
      if (!dialogState.open) return;
      if (event.key === "Escape") {
        event.preventDefault();
        onCancel();
      }
    };

    watch(
      () => dialogState.open,
      async (open) => {
        if (open) {
          document.body.classList.add("modal-open");
          await nextTick();
          confirmButton.value?.focus();
        } else {
          document.body.classList.remove("modal-open");
        }
      },
      { immediate: true }
    );

    window.addEventListener("keydown", onKeydown);

    onBeforeUnmount(() => {
      document.body.classList.remove("modal-open");
      window.removeEventListener("keydown", onKeydown);
    });

    return {
      dialogState,
      confirmButton,
      confirmButtonClass,
      onConfirm,
      onCancel,
    };
  },
});
</script>

<style scoped>
.app-dialog-backdrop {
  position: fixed;
  inset: 0;
  z-index: 3000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1.5rem;
  background: rgba(15, 23, 42, 0.32);
  backdrop-filter: blur(2px);
}

.app-dialog {
  width: min(520px, 100%);
  border: 0;
}

.app-dialog-message {
  white-space: pre-line;
}
</style>
